import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, CalendarX, CircleCheck, FileWarning, Wallet } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { AntiguedadCartera } from "@/componentes/finanzas/AntiguedadCartera"
import { comoErrorDeDinero } from "@/componentes/finanzas/errorDeDinero"
import { documentoDeCartera } from "@/componentes/finanzas/exportarCartera"
import {
  abierta,
  indicadores,
  type Partida,
  vencida,
} from "@/componentes/finanzas/indicadoresCartera"
import { ModalAbono } from "@/componentes/finanzas/ModalAbono"
import { ModalSoportesCxp, type SoporteCxp } from "@/componentes/finanzas/ModalSoportesCxp"
import { type FilaSaldo, TablaSaldos } from "@/componentes/finanzas/TablaSaldos"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { monto as comoCifra } from "@/componentes/tesoreria/dinero"
import type { Tono } from "@/componentes/ui/Insignia"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { tienePermiso } from "@/lib/autorizacion"
import { hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Cxp = {
  id: number
  proveedor_id: number
  concepto: string
  monto: string
  monto_pagado: string
  moneda: "USD" | "BS"
  estado: string
  fecha_vencimiento: string | null
  /** Lo que MOTIVA la deuda. Vacío mientras no se adjunte ninguno. */
  documentos: SoporteCxp[]
  /** (RM-3) Efectiva o borrador proyectado: lo decide el servidor. */
  efectiva?: boolean
  /** Si es el espejo de una CxP del 2.0 (18/10): su id allí y cómo está. Sólo informa. */
  sistema2_clave: string | null
  sistema2_total: string | null
  sistema2_pagado: string | null
  estado_sistema2: "PAGADA" | "ABONADA" | "PENDIENTE" | "ANULADA" | null
  /** La orden de pago que la está pagando, si hay una en curso. */
  orden_en_curso: { id: number; codigo: string; estado: string } | null
}

/**
 * La insignia del espejo del 2.0 (18/10): cómo está ALLÍ la cuenta. «Pagada en el 2.0»
 * con saldo aquí quiere decir que se pagó desde un banco del 3.0 —Binance, Provincial—
 * y falta su orden de pago aquí; desde otro banco, el 3.0 la paga sola.
 */
const INSIGNIA_2: Record<NonNullable<Cxp["estado_sistema2"]>, { texto: string; tono: Tono }> = {
  PAGADA: { texto: "Pagada en el 2.0", tono: "exito" },
  ABONADA: { texto: "Abonada en el 2.0", tono: "info" },
  PENDIENTE: { texto: "Pendiente en el 2.0", tono: "aviso" },
  ANULADA: { texto: "Anulada en el 2.0", tono: "peligro" },
}

type Proveedor = {
  id: number
  nombre: string
}

/**
 * Bandeja de cuentas por pagar a proveedores.
 *
 * ⚠ **Esta pantalla no es alcanzable desde el portal.** Una CxP lleva el costo del
 * proveedor, que es el margen de la agencia: el importador no la ve ni siquiera para
 * su propio expediente. Y no lo garantiza esta ruta sino el servidor, con
 * `requiere_interno`, que devuelve 404 —no 403— antes de mirar el permiso. Esconder
 * el enlace no es un permiso.
 *
 * ⚠ **Un abono parcial deja la cuenta `PARCIAL`, no `PAGADO`.** Eso lo decide
 * `liquidacion.py` recalculando desde el ledger de abonos, y por eso aquí no se
 * escribe ningún estado: se manda el importe y se relee la fila. Fijar el estado
 * desde el cliente es exactamente el segundo de los cuatro fallos del 2.0.
 */
export function CuentasPorPagar() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [enAbono, setEnAbono] = useState<number | null>(null)
  const [enSoportes, setEnSoportes] = useState<number | null>(null)
  const [ordenAbierta, setOrdenAbierta] = useState<number | null>(null)
  // Lo que filtra la tabla al pulsar una tarjeta (16/10).
  const [filtro, setFiltro] = useState<"abiertas" | "vencidas" | "sin_soporte" | null>(null)

  const consulta = useQuery({
    queryKey: ["cxp"],
    queryFn: () => api.get<Cxp[]>("/api/v1/cxp"),
  })

  const proveedores = useQuery({
    queryKey: ["proveedores"],
    queryFn: () => api.get<Proveedor[]>("/api/v1/proveedores"),
    enabled: tienePermiso(usuario, "proveedor:ver"),
  })

  const abonar = useMutation({
    meta: { exito: "Abono registrado" },
    mutationFn: ({ id, monto }: { id: number; monto: string }) =>
      api.post(`/api/v1/cxp/${id}/abonar`, { monto }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cxp"] })
      setEnAbono(null)
    },
  })

  // La orden de pago de una CxP (18/10): nace en BORRADOR y se abre su ficha para
  // revisarla y enviarla a aprobación. Al ejecutarse en Tesorería, abona esta CxP.
  const generarOrden = useMutation({
    meta: { exito: "Orden de pago redactada" },
    mutationFn: (id: number) =>
      api.post<{ orden_id: number; codigo: string }>(`/api/v1/cxp/${id}/orden-pago`, {}),
    onSuccess: (orden) => {
      clienteQuery.invalidateQueries({ queryKey: ["cxp"] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      setOrdenAbierta(orden.orden_id)
    },
  })

  if (consulta.isPending) {
    return <p className="p-6">Cargando…</p>
  }
  if (consulta.isError) {
    return (
      <p className="flex items-center gap-2 p-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} aria-hidden={true} /> No se pudo cargar la cartera.
      </p>
    )
  }

  const hoy = hoyLocal()
  const partida = (f: Cxp): Partida => ({
    monto: f.monto,
    aplicado: f.monto_pagado,
    moneda: f.moneda,
    vence: f.fecha_vencimiento,
    saldada: f.estado === "PAGADO",
    anulada: f.estado === "ANULADA",
    tercero: f.proveedor_id,
  })
  const kpi = indicadores(consulta.data.map(partida), hoy)
  // Abiertas sin el documento que motiva la deuda: se paga algo que nadie respaldó.
  const sinSoporte = consulta.data.filter((f) => abierta(partida(f)) && f.documentos.length === 0)
  const kpiSin = indicadores(sinSoporte.map(partida), hoy)
  const visibles = consulta.data.filter((f) =>
    filtro === "abiertas"
      ? abierta(partida(f))
      : filtro === "vencidas"
        ? vencida(partida(f), hoy)
        : filtro === "sin_soporte"
          ? sinSoporte.includes(f)
          : true,
  )
  const alternar = (f: typeof filtro) => setFiltro((actual) => (actual === f ? null : f))
  const puedeOrden = tienePermiso(usuario, "orden_pago:crear")
  const puedeAbonar = tienePermiso(usuario, "cxp:pagar")
  const puedeVerOrden = tienePermiso(usuario, "orden_pago:ver")
  // ⚠ Una CxP espejo del 2.0 se paga con una ORDEN, no con un abono suelto: el dinero
  // sale por Tesorería y la orden lo abona al ejecutarse. La hecha a mano se abona.
  const accionDe = (f: Cxp): string | null => {
    if (f.orden_en_curso) return puedeVerOrden ? f.orden_en_curso.codigo : null
    // (23/10, RM-3) Un borrador (servicio en curso y sin soporte) no se paga: primero el
    // documento. Lo decide el servidor (`efectiva`, `cxp_ciclo`), no la pantalla.
    // Subir el soporte pide `cxp:pagar` en el servidor (`subir_soporte`): se ofrece igual.
    if (f.sistema2_clave && f.efectiva === false) return puedeAbonar ? "Cargar soporte" : null
    if (f.sistema2_clave) return puedeOrden ? "Generar orden de pago" : null
    return puedeAbonar ? "Abonar" : null
  }
  const filas: FilaSaldo[] = visibles.map((f) => ({
    id: f.id,
    concepto: f.concepto,
    monto: f.monto,
    aplicado: f.monto_pagado,
    moneda: f.moneda,
    saldada: f.estado === "PAGADO",
    anulada: f.estado === "ANULADA",
    accion: accionDe(f),
    insignia: f.estado_sistema2
      ? {
          ...INSIGNIA_2[f.estado_sistema2],
          titulo: `${f.sistema2_clave} en el 2.0: ${comoCifra(f.sistema2_pagado ?? "0")} pagados de ${comoCifra(f.sistema2_total ?? "0")} ${f.moneda}`,
        }
      : undefined,
  }))
  const alPulsar = (id: number) => {
    const f = consulta.data.find((x) => x.id === id)
    if (!f) return
    if (f.orden_en_curso) return setOrdenAbierta(f.orden_en_curso.id)
    if (f.sistema2_clave && f.efectiva === false) return setEnSoportes(id)
    if (f.sistema2_clave) return generarOrden.mutate(id)
    abonar.reset()
    setEnAbono(id)
  }

  const cuenta = consulta.data.find((f) => f.id === enAbono) ?? null
  const conSoportes = consulta.data.find((f) => f.id === enSoportes) ?? null
  const nombreDe = new Map((proveedores.data ?? []).map((p) => [p.id, p.nombre]))

  const subtitulo =
    filtro === "abiertas"
      ? "Sólo las que tienen saldo"
      : filtro === "vencidas"
        ? "Sólo las vencidas"
        : filtro === "sin_soporte"
          ? "Borradores sin soporte"
          : "Todas las cuentas por pagar"
  const datos = consulta.data
  const documento = () =>
    documentoDeCartera({
      titulo: "Cuentas por pagar",
      subtitulo,
      etiquetaTercero: "Proveedor",
      etiquetaReferencia: "Clave en el 2.0",
      etiquetaAplicado: "Pagado",
      conEmision: false,
      filas: visibles.map((f) => ({
        tercero: nombreDe.get(f.proveedor_id) ?? `Proveedor ${f.proveedor_id}`,
        concepto: f.concepto,
        referencia: f.sistema2_clave,
        vence: f.fecha_vencimiento,
        estado: [
          f.estado,
          f.efectiva === false ? "borrador sin soporte" : null,
          f.orden_en_curso ? `orden ${f.orden_en_curso.codigo}` : null,
        ]
          .filter(Boolean)
          .join(", "),
        estado2: f.estado_sistema2 ? INSIGNIA_2[f.estado_sistema2].texto : null,
        moneda: f.moneda,
        monto: f.monto,
        aplicado: f.monto_pagado,
      })),
      antiguedadDe: {
        titulo: "Antigüedad de las cuentas por pagar",
        partidas: datos.map(partida),
        hoy,
      },
    })

  return (
    <section className="space-y-4 p-6">
      {visibles.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar titulo="Cuentas por pagar" subtitulo={subtitulo} documento={documento} />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo="Por pagar"
          detalle="Lo que falta, cuenta por cuenta"
          Icono={Wallet}
          color="var(--primary)"
          cantidad={kpi.abiertas.cuentas}
          montos={kpi.abiertas.montos}
          pie={`A ${kpi.terceros} ${kpi.terceros === 1 ? "proveedor" : "proveedores"}`}
          activa={filtro === "abiertas"}
          onClick={() => alternar("abiertas")}
        />
        <TarjetaKpi
          titulo="Vencidas"
          detalle="Con saldo y la fecha ya pasada"
          Icono={CalendarX}
          color="var(--destructive)"
          cantidad={kpi.vencidas.cuentas}
          montos={kpi.vencidas.montos}
          activa={filtro === "vencidas"}
          onClick={() => alternar("vencidas")}
        />
        <TarjetaKpi
          titulo="Pagado"
          detalle="Lo abonado a proveedores"
          Icono={CircleCheck}
          color="var(--success)"
          cantidad={kpi.aplicado.cuentas}
          montos={kpi.aplicado.montos}
          pie={kpi.aplicado.cuentas === 1 ? "cuenta saldada" : "cuentas saldadas"}
        />
        <TarjetaKpi
          titulo="Borrador sin soporte"
          detalle="Sin nota de despacho o factura: no se pagan ni suman en el Resumen macro"
          Icono={FileWarning}
          color="var(--warning)"
          cantidad={kpiSin.abiertas.cuentas}
          montos={kpiSin.abiertas.montos}
          activa={filtro === "sin_soporte"}
          onClick={() => alternar("sin_soporte")}
        />
      </div>

      <AntiguedadCartera
        partidas={consulta.data.map(partida)}
        hoy={hoy}
        titulo="Antigüedad de las cuentas por pagar"
      />

      <TablaSaldos
        filas={filas}
        etiquetaAplicado="Pagado"
        accion="Abonar"
        onAccion={puedeAbonar || puedeOrden || puedeVerOrden ? alPulsar : undefined}
        accionSecundaria="Soportes"
        onAccionSecundaria={(id) => setEnSoportes(id)}
      />

      {generarOrden.error instanceof ErrorApi && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo redactar la orden: {generarOrden.error.message}
        </p>
      )}

      {ordenAbierta !== null && (
        <FichaOrden ordenId={ordenAbierta} onCerrar={() => setOrdenAbierta(null)} />
      )}

      {conSoportes && (
        <ModalSoportesCxp
          cxpId={conSoportes.id}
          concepto={conSoportes.concepto}
          soportes={conSoportes.documentos}
          puedeEscribir={tienePermiso(usuario, "cxp:pagar")}
          onCerrar={() => setEnSoportes(null)}
        />
      )}

      {cuenta && (
        <ModalAbono
          contraparte={nombreDe.get(cuenta.proveedor_id) ?? cuenta.concepto}
          monto={cuenta.monto}
          montoPagado={cuenta.monto_pagado}
          moneda={cuenta.moneda}
          error={comoErrorDeDinero(abonar.error)}
          onAbonar={(monto) => abonar.mutate({ id: cuenta.id, monto })}
          onCerrar={() => setEnAbono(null)}
        />
      )}
    </section>
  )
}
